import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

// Only log in development mode
if (process.env.NODE_ENV === 'development') {
console.log('Supabase Config Check:', {
  url: supabaseUrl,
  hasKey: !!supabaseKey,
  urlValid: supabaseUrl && (supabaseUrl.startsWith('http://') || supabaseUrl.startsWith('https://'))
});
}

if (!supabaseUrl || !supabaseKey) {
  console.error('Missing Supabase environment variables. Please check your .env file.');
}

export const supabase = createClient(
  supabaseUrl || 'https://placeholder.supabase.co', 
  supabaseKey || 'placeholder-key'
);

// Auth helper functions
export const authHelpers = {
  // Sign up with email + password (stored in registered_users via backend).
  // `payload` must include email, password, and optional name, dob, place, tob, birthTimeUnknown, question, phone.
  signUpWithPassword: async (payload) => {
    try {
      const API_URL = process.env.NODE_ENV === 'production'
        ? (process.env.REACT_APP_PRODUCTION_API_URL || 'https://api.kundlishastra.com')
        : (process.env.REACT_APP_API_URL || 'http://localhost:5001');

      const response = await fetch(`${API_URL}/api/simple-auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok) return { data: null, error: { message: data.error } };

      const sessionData = {
        user: data.user,
        sessionId: data.sessionId,
        expires: data.expires,
      };
      if (data.defaultProfile?.data?.planets) {
        sessionData.pendingDashboard = {
          profileId: data.defaultProfile.id,
          chartData: data.defaultProfile.data,
          birthData: {
            name: data.defaultProfile.name,
            date: data.defaultProfile.dob,
            time: data.defaultProfile.tob,
            place: data.defaultProfile.place,
            question: data.defaultProfile.question || '',
            email: data.user?.email,
            latitude: data.defaultProfile.latitude ?? null,
            longitude: data.defaultProfile.longitude ?? null,
          },
        };
      }
      if (data.profileWarning) sessionData.profileWarning = data.profileWarning;
      localStorage.setItem('kundli-session', JSON.stringify(sessionData));
      return { data: sessionData, error: null };
    } catch (err) {
      return { data: null, error: { message: 'Network error. Please try again.' } };
    }
  },

  /** Load full `registered_users` row (e.g. occupation) using session. */
  fetchRegisteredUser: async () => {
    try {
      const sessionData = JSON.parse(localStorage.getItem('kundli-session') || '{}');
      const { user, sessionId } = sessionData;
      if (!user?.email || !sessionId) {
        return { data: null, error: { message: 'Not authenticated' } };
      }
      const API_URL = process.env.NODE_ENV === 'production'
        ? (process.env.REACT_APP_PRODUCTION_API_URL || 'https://api.kundlishastra.com')
        : (process.env.REACT_APP_API_URL || 'http://localhost:5001');
      const res = await fetch(
        `${API_URL}/api/user-profile/me?email=${encodeURIComponent(user.email)}`,
        { headers: { 'x-session-id': sessionId } }
      );
      const json = await res.json();
      if (!res.ok) return { data: null, error: { message: json.error || 'Failed to load user' } };
      return { data: json.data || null, error: null };
    } catch (e) {
      return { data: null, error: { message: e.message || 'Network error' } };
    }
  },

  /**
   * Update occupation for legacy users (same allowed values as signup).
   */
  updateRegisteredOccupation: async (email, occupation) => {
    try {
      const sessionData = JSON.parse(localStorage.getItem('kundli-session') || '{}');
      const sessionId = sessionData.sessionId;
      if (!sessionId) return { data: null, error: { message: 'Not authenticated' } };
      const API_URL = process.env.NODE_ENV === 'production'
        ? (process.env.REACT_APP_PRODUCTION_API_URL || 'https://api.kundlishastra.com')
        : (process.env.REACT_APP_API_URL || 'http://localhost:5001');
      const res = await fetch(`${API_URL}/api/user-profile/update`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'x-session-id': sessionId },
        body: JSON.stringify({ email, occupation }),
      });
      const json = await res.json();
      if (!res.ok) return { data: null, error: { message: json.error || 'Update failed' } };
      return { data: json.data, error: null };
    } catch (e) {
      return { data: null, error: { message: e.message || 'Network error' } };
    }
  },

  // Sign in with email + password (verified against registered_users via backend)
  signInWithPassword: async (email, password) => {
    try {
      const API_URL = process.env.NODE_ENV === 'production'
        ? (process.env.REACT_APP_PRODUCTION_API_URL || 'https://api.kundlishastra.com')
        : (process.env.REACT_APP_API_URL || 'http://localhost:5001');

      const response = await fetch(`${API_URL}/api/simple-auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await response.json();
      if (!response.ok) return { data: null, error: { message: data.error } };

      const sessionData = {
        user: data.user,
        sessionId: data.sessionId,
        expires: data.expires,
      };
      localStorage.setItem('kundli-session', JSON.stringify(sessionData));
      return { data: sessionData, error: null };
    } catch (err) {
      return { data: null, error: { message: 'Network error. Please try again.' } };
    }
  },

  // Forgot-password flow — single action-based endpoint (send OTP / verify OTP / reset password).
  _forgotPasswordRequest: async (payload) => {
    try {
      const API_URL = process.env.NODE_ENV === 'production'
        ? (process.env.REACT_APP_PRODUCTION_API_URL || 'https://api.kundlishastra.com')
        : (process.env.REACT_APP_API_URL || 'http://localhost:5001');

      const response = await fetch(`${API_URL}/api/simple-auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok) return { data: null, error: { message: data.error || 'Request failed' } };
      return { data, error: null };
    } catch (err) {
      return { data: null, error: { message: 'Network error. Please try again.' } };
    }
  },

  /** Step 1: request a password-reset OTP for `email`. */
  requestPasswordReset: async (email) => {
    return authHelpers._forgotPasswordRequest({ action: 'forgot_password', email });
  },

  /** Step 2: verify the OTP without consuming it. */
  verifyPasswordResetOtp: async (email, otp) => {
    return authHelpers._forgotPasswordRequest({ action: 'verify_reset_otp', email, otp });
  },

  /** Step 3: consume the OTP and set the new password. */
  resetPasswordWithOtp: async (email, otp, newPassword) => {
    return authHelpers._forgotPasswordRequest({ action: 'reset_password', email, otp, newPassword });
  },

  // Sign out
  signOut: async () => {
    try {
      // Sign out from Supabase
      await supabase.auth.signOut();
      
      // Clear custom session data
      localStorage.removeItem('kundli-session');
      localStorage.removeItem('supabase-session');
      
      return { error: null };
    } catch (error) {
      console.error('Logout error:', error);
    return { error };
    }
  },

  // Get current session from localStorage
  getSession: async () => {
    try {
      const sessionData = JSON.parse(localStorage.getItem('kundli-session') || '{}');
      
      if (!sessionData.user || !sessionData.sessionId) {
        return { session: null, error: null };
      }

      // Check if session is expired
      if (sessionData.expires && sessionData.expires < Date.now()) {
        if (process.env.NODE_ENV === 'development') {
        console.log('❌ Session expired:', new Date(sessionData.expires), 'Current time:', new Date());
        }
        localStorage.removeItem('kundli-session');
        return { session: null, error: null };
      }
      
      if (process.env.NODE_ENV === 'development') {
      console.log('✅ Session valid until:', sessionData.expires ? new Date(sessionData.expires) : 'No expiry set');
      }

      // Verify session with backend
      const API_URL = process.env.NODE_ENV === 'production' 
        ? (process.env.REACT_APP_PRODUCTION_API_URL || 'https://api.kundlishastra.com')
        : (process.env.REACT_APP_API_URL || 'http://localhost:5001');
        
      // Re-hydrate Supabase client session from stored tokens
      if (sessionData.access_token && sessionData.refresh_token) {
        try {
          await supabase.auth.setSession({
            access_token: sessionData.access_token,
            refresh_token: sessionData.refresh_token,
          });
        } catch (_) { /* non-fatal */ }
      }

      return { 
        session: sessionData, 
        error: null 
      };
    } catch (error) {
      console.error('Get session error:', error);
      return { session: null, error };
    }
  },

  // Get current user
  getCurrentUser: async () => {
    const { data: { user }, error } = await supabase.auth.getUser();
    return { user, error };
  },

};

// Profile helper functions
export const profileHelpers = {
  // Create new profile (uses API route to link with registered_user_id)
  createProfile: async (profileData) => {
    try {
      const API_URL = process.env.NODE_ENV === 'production' 
        ? (process.env.REACT_APP_PRODUCTION_API_URL || 'https://api.kundlishastra.com')
        : (process.env.REACT_APP_API_URL || 'http://localhost:5001');

      // Get session ID for authentication
      const sessionData = JSON.parse(localStorage.getItem('kundli-session') || '{}');
      const sessionId = sessionData.sessionId;

      if (!sessionId) {
        return { data: null, error: { message: 'Not authenticated' } };
      }

      // Use API route which handles registered_user_id linking
      const response = await fetch(`${API_URL}/api/profiles/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionId}`
        },
        body: JSON.stringify(profileData)
      });

      const result = await response.json();
      
      if (!response.ok) {
        console.error('Profile creation failed:', result);
        return { data: null, error: { message: result.error || 'Failed to create profile' } };
      }

      if (result.success && result.data) {
        return { data: result.data, error: null };
      } else {
        console.error('Unexpected response format:', result);
        return { data: null, error: { message: 'Unexpected response from server' } };
      }
    } catch (error) {
      console.error('Error creating profile:', error);
      return { data: null, error: { message: error.message || 'Network error' } };
    }
  },

  // Create a "Name Analysis" profile (no birth details yet) — used by the
  // onboarding page when the user picks "Name Analysis". Handled by a
  // dedicated backend service, fully independent of the Kundli engine.
  createNameAnalysisProfile: async ({ name, email }) => {
    try {
      const API_URL = process.env.NODE_ENV === 'production'
        ? (process.env.REACT_APP_PRODUCTION_API_URL || 'https://api.kundlishastra.com')
        : (process.env.REACT_APP_API_URL || 'http://localhost:5001');

      const sessionData = JSON.parse(localStorage.getItem('kundli-session') || '{}');
      const sessionId = sessionData.sessionId;
      if (!sessionId) {
        return { data: null, error: { message: 'Not authenticated' } };
      }

      const response = await fetch(`${API_URL}/api/name-analysis/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${sessionId}` },
        body: JSON.stringify({ name, email }),
      });
      const result = await response.json();
      if (!response.ok) {
        return { data: null, error: { message: result.error || 'Failed to create profile' } };
      }
      return { data: result.data, error: null };
    } catch (error) {
      return { data: null, error: { message: error.message || 'Network error' } };
    }
  },

  // Compute-only Name Analysis (no DB write) — used when Edit Profile / Add
  // New Member switch a profile into Name Analysis mode; the result is then
  // saved via updateProfileFull/createNameAnalysisProfile.
  previewNameAnalysis: async ({ name }) => {
    try {
      const API_URL = process.env.NODE_ENV === 'production'
        ? (process.env.REACT_APP_PRODUCTION_API_URL || 'https://api.kundlishastra.com')
        : (process.env.REACT_APP_API_URL || 'http://localhost:5001');

      const sessionData = JSON.parse(localStorage.getItem('kundli-session') || '{}');
      const sessionId = sessionData.sessionId;
      if (!sessionId) {
        return { data: null, error: { message: 'Not authenticated' } };
      }

      const response = await fetch(`${API_URL}/api/name-analysis/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${sessionId}` },
        body: JSON.stringify({ name }),
      });
      const result = await response.json();
      if (!response.ok) {
        return { data: null, error: { message: result.error || 'Failed to analyse name' } };
      }
      return { data: result.data, error: null };
    } catch (error) {
      return { data: null, error: { message: error.message || 'Network error' } };
    }
  },

  // Upgrade an existing profile (e.g. name-only → complete) or edit any
  // profile's full birth details + chart data in place — same profile id,
  // no new account/profile row.
  updateProfileFull: async ({ id, name, dob, tob, place, data, email, question }) => {
    try {
      const API_URL = process.env.NODE_ENV === 'production'
        ? (process.env.REACT_APP_PRODUCTION_API_URL || 'https://api.kundlishastra.com')
        : (process.env.REACT_APP_API_URL || 'http://localhost:5001');

      const response = await fetch(`${API_URL}/api/profiles/update-full`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, name, dob, tob, place, data, email, question }),
      });
      const result = await response.json();
      if (!response.ok) {
        return { data: null, error: { message: result.error || 'Failed to update profile' } };
      }
      return { data: result.data, error: null };
    } catch (error) {
      return { data: null, error: { message: error.message || 'Network error' } };
    }
  },

  // Get all profiles for the logged-in user (filtered by registered_user_id via email)
  getProfiles: async (email) => {
    if (!email) return { data: [], error: null };
    try {
      const API_URL = process.env.NODE_ENV === 'production'
        ? (process.env.REACT_APP_PRODUCTION_API_URL || 'https://api.kundlishastra.com')
        : (process.env.REACT_APP_API_URL || 'http://localhost:5001');
      const response = await fetch(`${API_URL}/api/profiles/my?email=${encodeURIComponent(email)}`);
      const result = await response.json();
      if (!response.ok) return { data: [], error: { message: result.error } };
      return { data: result.data || [], error: null };
    } catch (err) {
      return { data: [], error: { message: err.message } };
    }
  },

  // Update profile
  updateProfile: async (id, updates) => {
    const { data, error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    return { data, error };
  },

  // Delete profile
  deleteProfile: async (id) => {
    const { data, error } = await supabase
      .from('profiles')
      .delete()
      .eq('id', id);
    return { data, error };
  }
};
